<script setup lang="ts">
import EntryAttachments from '@/core/attachments/EntryAttachments.vue';
import { EntryAttachmentType } from 'common/enum/entry.attachment.enum';

import { useClassStudents } from '@/composables/student/useClassStudents';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import type { Components } from '@/api/generated/api';
import { useNote } from '@/composables/note/useNote';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import ThreeDotsActionDialog from '@/core/dialogs/ThreeDotsActionDialog.vue';
import InlineDatePicker from '@/core/elements/InlineDatePicker.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import FormTextarea from '@/core/forms/FormTextarea.vue';
import CompetenceSelection from '@/core/selects/CompetenceSelection.vue';
import StudentSelection from '@/core/selects/StudentSelection.vue';
import { EObservationTone, type IDropdownListAction, type ISelectedCompetence, type TStudentLike } from '@/interfaces';
import { toCompetenceSelectionRecord } from '@/utils/competence.domain.utils';
import {
  areAllEligibleStudentsSelected,
  getEligibleStudentsForSubject,
  resolveStudentSelection,
} from '@/utils/students.utils';
import { NOTE_TEXT_MAX_LENGTH } from 'common/constants/note.constants';
import { EIcon } from 'common/enum/misc.enum';
import { isNotNullish, toRecord } from 'common/utils/array.utils';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

interface INoteDetailsDialogShowParams {
  noteId: number;
}

const emit = defineEmits<{
  'update:note': [noteId: number];
  'delete:note': [noteId: number];
}>();

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    onClose?: () => Promise<void> | void;
  }>(),
  { disabled: false, onClose: undefined },
);

const { t } = useI18n();
const { getNoteById, updateNote, deleteNote } = useNote();
const { allSchoolSubjects } = useSchoolSubject();
const { allCompetenceDomains } = useCompetenceDomain();
const { currentClass } = useCurrentClass();
const { allStudents } = useClassStudents(() => currentClass.value?.id);
const { confirm } = useConfirmDialog();

const dialogRef = useTemplateRef('dialogRef');
const datePickerRef = useTemplateRef<InstanceType<typeof InlineDatePicker>>('datePickerRef');
const studentSelectionRef = useTemplateRef('studentSelectionRef');
const note = ref<Components.Schemas.NoteDto | null>(null);
const noteText = ref('');
const isSavingText = ref(false);
const isSavingRelations = ref(false);
const isSavingDate = ref(false);

const schoolSubjectId = computed(() => note.value?.schoolSubject?.id ?? null);

const eligibleStudents = computed(() => getEligibleStudentsForSubject(allStudents.value, schoolSubjectId.value));
const studentById = computed(() =>
  toRecord(
    allStudents.value,
    (student) => student.id,
    (student) => student,
  ),
);

const selectedStudents = computed<TStudentLike[]>(() => {
  if (!note.value) return [];
  return note.value.students.map((student) => studentById.value[student.id] ?? student);
});

// Keep documented students removable after they lose access; once removed, they leave the picker as well.
const selectableStudents = computed(() =>
  resolveStudentSelection<TStudentLike>({
    allEligibleStudentsSelected: true,
    eligibleStudents: eligibleStudents.value,
    selectedStudents: selectedStudents.value,
  }),
);

const allEligibleStudentsSelected = computed(() =>
  areAllEligibleStudentsSelected(selectedStudents.value, eligibleStudents.value),
);

const competenceSelectionById = computed(() => toCompetenceSelectionRecord(allCompetenceDomains.value));

const selectedCompetences = computed(() =>
  (note.value?.competences ?? [])
    .map((noteCompetence) => competenceSelectionById.value[noteCompetence.competenceId])
    .filter(isNotNullish),
);

const dropdownActions = computed<IDropdownListAction[]>(() => [
  { label: t('note.delete'), iconBefore: EIcon.DELETE, style: 'error', action: () => void handleDelete() },
]);

const setInlineText = (text: string): void => {
  noteText.value = text;
};

const saveInlineText = async (): Promise<void> => {
  const currentNote = note.value;
  if (!currentNote || props.disabled || isSavingText.value) return;

  const previousText = currentNote.text;
  const nextText = noteText.value.trim();

  if (!nextText) {
    setInlineText(previousText);
    return;
  }
  if (nextText === previousText) {
    setInlineText(previousText);
    return;
  }

  isSavingText.value = true;
  try {
    const updated = await updateNote(currentNote.id, { text: nextText });
    if (note.value?.id === currentNote.id) note.value = updated;
    setInlineText(updated.text);
    emit('update:note', updated.id);
  } catch {
    setInlineText(previousText);
  } finally {
    isSavingText.value = false;
  }
};

const updateRelations = async (dto: Components.Schemas.UpdateNoteDto): Promise<void> => {
  if (!note.value || isSavingRelations.value || props.disabled) return;

  isSavingRelations.value = true;
  try {
    const updated = await updateNote(note.value.id, dto);
    if (note.value.id === updated.id) note.value = updated;
    emit('update:note', updated.id);
  } finally {
    isSavingRelations.value = false;
  }
};

const updateDate = async (date: string): Promise<void> => {
  const currentNote = note.value;
  if (!currentNote || props.disabled || isSavingDate.value || currentNote.date === date) return;

  isSavingDate.value = true;
  try {
    const updated = await updateNote(currentNote.id, { date });
    if (note.value?.id === currentNote.id) note.value = updated;
    emit('update:note', updated.id);
  } finally {
    isSavingDate.value = false;
  }
};

const updateStudents = (students: TStudentLike[]): void => {
  void updateRelations({ studentIds: students.map((student) => student.id) });
};

const selectAllEligibleStudents = (): void => {
  updateStudents(selectableStudents.value);
};

const findCompetence = (competenceId: number): ISelectedCompetence | null =>
  competenceSelectionById.value[competenceId] ?? null;

const addCompetence = (competenceId: number): void => {
  if (!note.value || note.value.competences.some((competence) => competence.competenceId === competenceId)) return;

  const selection = findCompetence(competenceId);
  const competenceSchoolSubjectId = selection?.domain.schoolSubjectId;
  if (competenceSchoolSubjectId == null) return;
  if (schoolSubjectId.value != null && competenceSchoolSubjectId !== schoolSubjectId.value) return;

  const competenceIds = [...note.value.competences.map((competence) => competence.competenceId), competenceId];
  if (schoolSubjectId.value != null) {
    void updateRelations({ competenceIds });
    return;
  }

  const nextEligibleStudents = getEligibleStudentsForSubject(allStudents.value, competenceSchoolSubjectId);
  const nextEligibleIds = new Set(nextEligibleStudents.map((student) => student.id));
  const studentIds = allEligibleStudentsSelected.value
    ? nextEligibleStudents.map((student) => student.id)
    : selectedStudents.value.map((student) => student.id).filter((studentId) => nextEligibleIds.has(studentId));

  void updateRelations({
    schoolSubjectId: competenceSchoolSubjectId,
    studentIds,
    competenceIds,
  });
};

const removeCompetence = (competenceId: number): void => {
  if (!note.value) return;
  void updateRelations({
    competenceIds: note.value.competences
      .map((competence) => competence.competenceId)
      .filter((id) => id !== competenceId),
  });
};

const handleDelete = async (): Promise<void> => {
  if (!note.value) return;

  const confirmed = await confirm({
    title: t('note.deleteConfirm.title'),
    description: t('note.deleteConfirm.description'),
    confirmButtonLabel: t('note.deleteConfirm.confirmButtonLabel'),
    declineButtonLabel: t('note.deleteConfirm.declineButtonLabel'),
    theme: 'danger',
  });
  if (!confirmed) return;

  const noteId = note.value.id;
  await deleteNote(noteId);
  emit('delete:note', noteId);
  dialogRef.value?.hide();
};

const show = async (params: INoteDetailsDialogShowParams): Promise<void> => {
  const loaded = await getNoteById(params.noteId);
  if (!loaded) return;

  note.value = loaded;
  noteText.value = loaded.text;
  isSavingText.value = false;
  isSavingDate.value = false;
  datePickerRef.value?.hide();
  studentSelectionRef.value?.hide();
  await dialogRef.value?.show();
};

const hide = (): void => {
  datePickerRef.value?.hide();
  studentSelectionRef.value?.hide();
  dialogRef.value?.hide();
};

const handleDialogHide = async (): Promise<void> => {
  datePickerRef.value?.hide();
  studentSelectionRef.value?.hide();
  await props.onClose?.();
};

defineExpose({ show, hide });
</script>

<template>
  <DialogBase
    ref="dialogRef"
    no-padding
    content-width-style="w-[640px] bg-surface-note !shadow-popup"
    dialog-height-style="h-[480px] max-md:h-dvh"
    header-theme="note"
    @hide="handleDialogHide"
  >
    <template v-if="note" #title>
      <div class="flex min-w-0 flex-col">
        <div class="flex items-center gap-sm">
          <span class="text-base font-semibold text-primary">{{ $t('note.title') }}</span>
          <SchoolSubjectBadge v-if="note.schoolSubject" :school-subject="note.schoolSubject" />
        </div>
        <InlineDatePicker
          ref="datePickerRef"
          :date="note.date"
          :disabled="props.disabled"
          :loading="isSavingDate"
          theme="note"
          @update:date="updateDate"
        />
      </div>
    </template>

    <template #actions>
      <ThreeDotsActionDialog v-if="!props.disabled" :actions="dropdownActions" :min-width="240" theme="note" />
    </template>

    <div v-if="note" class="flex min-h-full flex-col bg-surface-note">
      <div class="flex shrink-0 flex-col items-start gap-md px-content pb-content max-md:px-lg">
        <StudentSelection
          ref="studentSelectionRef"
          :all-eligible-students-selected="allEligibleStudentsSelected"
          :eligible-students="selectableStudents"
          :selected-students="selectedStudents"
          :disabled="isSavingRelations"
          :read-only="props.disabled"
          mobile-drawer
          :tone="EObservationTone.Note"
          @select:all-eligible-students="selectAllEligibleStudents"
          @update:students="updateStudents"
        />

        <FormTextarea
          :name="`note-details-text-${note.id}`"
          class="w-full shrink-0"
          :aria-label="$t('note.edit')"
          auto-resize
          :commit-on-enter="false"
          :maxlength="NOTE_TEXT_MAX_LENGTH"
          :rows="1"
          :readonly="props.disabled || isSavingText"
          theme="inline"
          textarea-class="resize-none text-base text-primary placeholder:!text-secondary placeholder:!opacity-100"
          :value="noteText"
          :placeholder="$t('note.addNotesPlaceholder')"
          @update:value="setInlineText"
          @commit="saveInlineText"
        />

        <EntryAttachments
          :key="note.id"
          :entry-type="EntryAttachmentType.Note"
          :entry-id="note.id"
          :materials="note.materials"
          :readonly="props.disabled"
          :can-edit="isSavingRelations ? false : undefined"
          @update:materials="updateRelations({ externalMaterialIds: $event })"
        />

        <CompetenceSelection
          :all-school-subjects="allSchoolSubjects"
          :competence-domains="allCompetenceDomains"
          :disabled="isSavingRelations"
          :read-only="props.disabled"
          mobile-drawer
          :school-subject-id="schoolSubjectId"
          :selected-competences="selectedCompetences"
          :tone="EObservationTone.Note"
          @add:competence="addCompetence"
          @remove:competence="removeCompetence"
        />
      </div>
    </div>
  </DialogBase>
</template>
